import { Box, Paper, Skeleton, Stack, Typography } from "@mui/material";
import React, { useState } from "react";
import {
  Bar,
  BarChart,
  ComposedChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import bookingSourceData from "./__mock__/bookingSourcePerformance.json";
import SubContainer from "./SubContainer";
import useBookingSourcePerformance from "./hooks/useBookingSourcePerformance";
import { useSelector } from "react-redux";
import { getCurrencySymbol, roundIfDecimal } from "../../../Utils/Utils";
const bookingMetricList = ["Revenue", "Rooms Sold"];

const CustomTooltip = ({
  active,
  payload,
  label,
  currency = "INR",
  metric,
  currencySymbol,
  doComparison,
}) => {
  if (!active || !payload?.length) return null;

  const currentYear = new Date().getFullYear();
  const isCurrencyMetric = metric === "Revenue";

  const formatValue = (value) => {
    if (isCurrencyMetric) {
      return `${currencySymbol}${value.toLocaleString()}`;
    }
    return value;
  };

  const getLabel = (entryName) => {
    if (doComparison) {
      return entryName.startsWith("ly_") ? currentYear - 1 : currentYear;
    }
    return metric;
  };

  return (
    <Box
      sx={{
        backgroundColor: "#1F2937",
        border: "1px solid #4B5563",
        borderRadius: "8px",
        padding: "0.75rem 1rem",
        boxShadow: "0 4px 12px rgba(0,0,0,0.3)",
        color: "#E5E7EB",
      }}
    >
      <Typography
        variant="body2"
        sx={{
          fontWeight: 600,
          marginBottom: "0.5rem",
          color: "#F3F4F6",
        }}
      >
        {label}
      </Typography>

      <Stack spacing={0.5}>
        {payload.map((entry, index) => (
          <Stack
            key={`item-${index}`}
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            spacing={2}
          >
            <Stack direction="row" alignItems="center" spacing={1}>
              {/* Colored indicator dot */}
              <Box
                sx={{
                  width: 10,
                  height: 10,
                  borderRadius: "50%",
                  backgroundColor: entry.color || "#60A5FA",
                }}
              />
              <Typography variant="body2" sx={{ color: "#D1D5DB" }}>
                {getLabel(entry.name)}
              </Typography>
            </Stack>
            <Typography variant="body2" sx={{ color: "#E5E7EB" }}>
              {formatValue(entry.value)}
            </Typography>
          </Stack>
        ))}
      </Stack>
    </Box>
  );
};

const BookingSourcePerformance = ({
  data,
  period,
  isLoading,
  doComparison,
  comparisonData,
}) => {
  const hotels = useSelector((state) => state.auth.hotels);
  const hotelCurrency = hotels[0]?.hotelCurrency || "INR";
  const currencySymbol = getCurrencySymbol(hotelCurrency);
  const {
    metric,
    handleMetricChange,
    xAxisKey,
    yAxisKey,
    chartData,
    mergedChartData,
  } = useBookingSourcePerformance({
    data,
    period,
    comparisonData,
  });

  const calculateChartHeight = (size = 0) => {
    return size > 5 ? size * 64 : "320px";
  };
  if (isLoading) {
    return (
      <Box height={"320px"}>
        <Skeleton
          variant="rounded"
          width={"100%"}
          height={320}
          animation="wave"
          sx={{
            borderRadius: "8px",
            "&.MuiSkeleton-root": {
              backgroundColor: "#b6bed350",
            },
          }}
        />
      </Box>
    );
  }
  return (
    <SubContainer spacing={2}>
      <Stack direction="row" justifyContent="space-between" alignItems="center">
        <Typography variant="h5" fontWeight={700} color={"white"}>
          Booking Source Performance
        </Typography>
        <Stack
          direction="row"
          spacing={1}
          bgcolor={"#1F2937"}
          border={"1px solid #4B5563"}
          padding={"0.25rem"}
          borderRadius={"0.5rem"}
        >
          {bookingMetricList.map((m) => (
            <Box
              key={m}
              onClick={() => handleMetricChange(m)}
              sx={{
                padding: "0.25rem 0.75rem",
                fontSize: "0.875rem",
                borderRadius: "0.375rem",
                whiteSpace: "nowrap",
                border: "none",
                cursor: "pointer",
                ...(metric !== m
                  ? {
                      color: "#9CA3AF",
                      backgroundColor: "transparent",
                    }
                  : {
                      backgroundColor: "#374151",
                      color: "#FFFFFF",
                    }),
                fontWeight: 600,
              }}
            >
              {m}
            </Box>
          ))}
        </Stack>
      </Stack>
      <Box
        sx={{
          height:
            calculateChartHeight(chartData?.length) * (doComparison ? 2 : 1),
        }}
      >
        <ResponsiveContainer width="100%" height={"100%"}>
          <ComposedChart
            data={mergedChartData}
            layout="vertical"
            margin={{ left: 48, right: 30 }}
          >
            <XAxis
              type="number"
              tickFormatter={(v) => {
                const isCurrencyMetric = metric === "Revenue";
                if (isCurrencyMetric) {
                  return `${currencySymbol}${roundIfDecimal(v, 0)}`;
                }
                return v;
              }}
              tick={{ fill: "#b6bed3" }}
            />
            <YAxis
              dataKey={yAxisKey}
              type="category"
              tick={{ fill: "#b6bed3" }}
            />
            <Tooltip
              content={(props) => (
                <CustomTooltip
                  {...props}
                  metric={metric}
                  currencySymbol={currencySymbol}
                  doComparison={doComparison}
                />
              )}
              cursor={{ fill: "transparent" }}
            />
            {doComparison && (
              <Bar
                dataKey={"ly_" + xAxisKey}
                fill="#4A5568"
                barSize={48}
                radius={[0, 8, 8, 0]}
                stroke="#718096"
              />
            )}
            <Bar
              dataKey={xAxisKey}
              fill="#4A5568"
              barSize={48}
              radius={[0, 8, 8, 0]}
              stroke="#718096"
            />
          </ComposedChart>
        </ResponsiveContainer>
      </Box>
    </SubContainer>
  );
};

export default BookingSourcePerformance;
